/**
 * Kusto (Azure Data Explorer / KQL) database queries
 *
 * Every "query" here runs through the normal query path (the S2 kusto adapter
 * allows `.show`, blocks every other dot-command). Kusto has no schema concept
 * — like SQLite, `id`/`name` are the object name and `schema_name` is left
 * unset (defaults to '' downstream).
 *
 * ## Why the `.show` commands carry no `| project`
 *
 * These queries must run unchanged against BOTH a real ADX cluster and an
 * Azure Monitor ADX proxy (App Insights `ade.applicationinsights.io`, Log
 * Analytics `ade.loganalytics.io`). The proxy implements its own, much
 * narrower management-command grammar and rejects anything outside it with an
 * HTTP 400 `MgmtCslFailedParsing`. Two rules, quoted from the proxy's own
 * parse errors:
 *
 *   1. A control command may not be piped. `.show tables | project x = y` is a
 *      400 — the grammar ends at the command. So every `.show` below is bare
 *      and its rows are reshaped in-process by `toKustoObjectRows`.
 *   2. The only verbs that exist after `.show` are: `capacity`, `cluster`,
 *      `database`, `databases`, `entity`, `function`, `functions`, `schema`,
 *      `tables`, `version`. `.show table <T> cslschema` is NOT among them,
 *      which is why `columns` is a `getschema` *query* — the query endpoint
 *      still allows pipes, so it can project straight into the row shape the
 *      engine-agnostic consumers already expect.
 *
 * `materializedViews` / `externalTables` have no proxy equivalent at all; the
 * Object Explorer omits those folders for proxy connections (see
 * `createDatabaseObjectTypeFolders`), so these commands only ever reach a real
 * cluster.
 *
 * Export surface mirrors `mysqlQueries.ts` exactly (verified key set):
 *   databases, tables, views, storedProcedures, functions,
 *   columns(schema, tableName), tableConstraints(s,t), tableIndexes(s,t),
 *   tableTriggers(s,t), tableDefaultConstraints(s,t), tableStatistics(s,t),
 *   tableOptions(s,t), columnEnrichments(s,t), partition(s,t),
 *   databaseProperties(databaseName), defaultDatabase, fallbackDatabases.
 * Plus kusto-specific object-listing keys consumed via the same
 * `queries[key]` lookup `getOracleObjects` already uses for engine-specific
 * folders: materializedViews, externalTables.
 *
 * `schema` parameters are accepted (for arity parity with the other engines'
 * `MetadataQueries`/`DatabaseQueries` shape) but unused — kusto tables are
 * addressed by name only.
 */

/** A query that is structurally valid KQL but always yields zero rows. */
const NO_ROWS = 'print __none = 1 | where 1 == 2';

/**
 * Escapes the content of a single-quoted KQL string literal. KQL non-verbatim
 * literals use C-style backslash escapes (NOT quote-doubling — that is only for
 * `@`-verbatim literals). Escape backslash first so a trailing `\` can't escape
 * the closing quote.
 */
function escapeKqlStringLiteral(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/** Kusto identifiers with non-simple characters need `['name']` bracket quoting. */
function quoteKustoName(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : `['${escapeKqlStringLiteral(name)}']`;
}

/**
 * Reshapes the rows of a bare `.show databases` / `.show tables` /
 * `.show functions` / `.show materialized-views` / `.show external tables`
 * result into the `{ id, name }` shape the engine-agnostic Object Explorer
 * consumers (`mapDatabaseObjectRows`, `databasesService`) read.
 *
 * A `| project` in the command itself would be the natural place for this, but
 * the ADX proxy rejects piped control commands — see the module comment.
 * Kusto's listing commands name their object column `TableName` (tables,
 * external tables), `Name` (functions, materialized views) or `DatabaseName`
 * (databases); no command returns more than one of them.
 */
export function toKustoObjectRows(
  rows: Record<string, unknown>[],
): { id: string; name: string }[] {
  return rows
    .map((row) => String(row.TableName ?? row.Name ?? row.DatabaseName ?? row.name ?? ''))
    .filter((name) => name.trim().length > 0)
    .map((name) => ({ id: name, name }));
}

export const kustoQueries = {
  /** Lists every database visible on the cluster (a proxy exposes exactly one). */
  databases: '.show databases',

  /** Kusto has no schema concept (see header) — an empty listing, like `schema_name`. */
  schemas: NO_ROWS,

  tables: '.show tables',

  /** Kusto has no view concept. */
  views: NO_ROWS,

  /** Kusto has no stored-procedure concept. */
  storedProcedures: NO_ROWS,

  functions: '.show functions',

  /** Kusto-specific object type — surfaced as its own Object Explorer folder. Cluster-only. */
  materializedViews: '.show materialized-views',

  /** Kusto-specific object type — surfaced as its own Object Explorer folder. Cluster-only. */
  externalTables: '.show external tables',

  /**
   * A `getschema` query rather than a control command: `.show table <T>
   * cslschema` does not exist on the ADX proxy, and `getschema` (a) works on
   * both endpoints, (b) is pipeable, so it projects straight into the
   * `column_name` / `data_type` / `ordinal_position` row shape that
   * `metadataService` and `mapColumnInfoRows` already consume, and (c) yields
   * CSL scalar types (`string`, `datetime`) — the vocabulary
   * `categorizeKustoColumn` expects. `ColumnOrdinal` is 0-based.
   *
   * Every Kusto column is nullable and none is a primary key.
   */
  columns: (_schema: string, tableName: string) =>
    `${quoteKustoName(tableName)} | getschema | project column_name = ColumnName, ` +
    'data_type = ColumnType, ordinal_position = ColumnOrdinal + 1, ' +
    'is_nullable = true, is_primary_key = false',

  /** Kusto has no constraint concept. */
  tableConstraints: (_schema: string, _tableName: string) => NO_ROWS,

  /** Kusto has no index concept. */
  tableIndexes: (_schema: string, _tableName: string) => NO_ROWS,

  /** Kusto has no trigger concept. */
  tableTriggers: (_schema: string, _tableName: string) => NO_ROWS,

  /** Kusto has no named default-constraint concept (column defaults don't exist). */
  tableDefaultConstraints: (_schema: string, _tableName: string) => NO_ROWS,

  /** Kusto has no query-optimizer statistics catalog exposed via KQL. */
  tableStatistics: (_schema: string, _tableName: string) => NO_ROWS,

  /** Kusto has no table-level storage-option catalog exposed via KQL. */
  tableOptions: (_schema: string, _tableName: string) => NO_ROWS,

  /** Kusto has no per-column enrichment catalog beyond the column schema itself. */
  columnEnrichments: (_schema: string, _tableName: string) => NO_ROWS,

  /** Kusto tables aren't partitioned in the SQL-engine sense. */
  partition: (_schema: string, _tableName: string) => NO_ROWS,

  /**
   * Can't filter to `databaseName` — a piped control command is a 400 on the
   * ADX proxy. Harmless: `mapDatabaseMetadata` names the database from its own
   * `databaseName` argument, and kusto exposes none of collation / owner /
   * encoding, so no field is read off the row itself.
   */
  databaseProperties: (_databaseName: string) => '.show databases',

  /**
   * Kusto has no fixed system database (no `master`/`information_schema`
   * equivalent) — the empty string signals "no known default"; the query
   * adapter (`kustoAdapter.execute`) already falls back to the connection's
   * configured database (or `NetDefaultDB`) whenever the requested database
   * is falsy, so this is a safe no-op default rather than a real database name.
   */
  defaultDatabase: '',

  /** No hardcoded system databases to offer as a listing-failure fallback. */
  fallbackDatabases: [] as string[],
};
